<template>
    <nav
        class="flex items-stretch gap-3 overflow-x-auto border-b border-border bg-background px-2 lg:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label="Primary"
    >
        <inertia-link
            v-for="item in links"
            :key="item.id"
            :href="item.href"
            class="group flex min-h-10 shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm whitespace-nowrap transition-colors"
            :class="
                isActive(item)
                    ? 'border-new-topol-blue bg-surface-card text-foreground dark:bg-muted'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
            "
        >
            <component :is="item.icon" class="size-4 shrink-0" />
            <span>{{ item.name }}</span>
            <span
                v-if="item.reserveBadge || hasCount(item)"
                class="flex size-4 items-center justify-center px-1 text-xs font-medium"
                :class="
                    isCountReady(item)
                        ? 'border border-border text-foreground'
                        : 'animate-pulse bg-new-neutral-200 text-transparent'
                "
            >
                {{ isCountReady(item) ? item.count : '' }}
            </span>
        </inertia-link>
    </nav>
</template>

<script lang="ts" setup>
import sidebar from '@/data/sidebar';
import sidebarNew from '@/data/sidebar-new';
import pluginSidebar from '@/data/pluginSidebar-new';
import { ILink } from '@/types/Atomic/ISidebarData';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';

const { links: kioskLinks } = sidebar();
const { links: appLinks } = sidebarNew();
const { links: pluginLinks } = pluginSidebar();

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const page = computed(() => usePage<ITopolPageProps>().url);

const isKiosk = computed(() => page.value.startsWith('/kiosk'));

const links = computed<ILink[]>(() => {
    if (isKiosk.value) return kioskLinks.value;
    const appType = globalProps.value.user?.app_type;
    if (appType === 'plugin') return pluginLinks.value;
    if (appType === 'pro' || appType === 'both' || appType === null) {
        return appLinks.value;
    }
    return [];
});

const isActive = (item: ILink): boolean => page.value.startsWith(item.id);

const hasCount = (item: ILink): boolean =>
    typeof item.count === 'number' && item.count > 0;

// `reserveBadge` items (e.g. Members) always show a count once loaded; until the
// async count arrives we render a skeleton so the badge slot reserves its width
// and the surrounding nav items don't shift when the number pops in.
const isCountReady = (item: ILink): boolean => typeof item.count === 'number';
</script>
